import * as SwitchPrimitive from '@radix-ui/react-switch';
import {
  forwardRef,
  useId,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactNode,
} from 'react';
import { cn } from '@/lib/utils';

export interface SwitchProps extends Omit<
  ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>,
  'onCheckedChange'
> {
  onCheckedChange?: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
}

export const Switch = forwardRef<ElementRef<typeof SwitchPrimitive.Root>, SwitchProps>(
  ({ className, label, description, onCheckedChange, id, disabled, ...props }, ref) => {
    const generated = useId();
    const controlId = id ?? generated;

    const control = (
      <SwitchPrimitive.Root
        ref={ref}
        id={controlId}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        className={cn(
          'peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border border-input bg-muted shadow-sm transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'data-[state=checked]:border-primary/60 data-[state=checked]:bg-primary/25',
          !label && className,
        )}
        {...props}
      >
        <SwitchPrimitive.Thumb
          className={cn(
            'pointer-events-none ms-0.5 block h-3.5 w-3.5 rounded-full bg-muted-foreground shadow-sm ring-0 transition-[margin,background-color,box-shadow] duration-150 motion-reduce:transition-none',
            'data-[state=checked]:ms-[18px] data-[state=checked]:bg-primary data-[state=checked]:shadow-[0_0_12px_-2px_hsl(var(--primary)/0.45)]',
          )}
        />
      </SwitchPrimitive.Root>
    );

    if (!label) return control;

    return (
      <div className={cn('flex items-start gap-2.5', className)}>
        <span className="flex h-5 items-center">{control}</span>
        <span className="grid gap-0.5">
          <label
            htmlFor={controlId}
            className={cn(
              'select-none text-sm leading-5 text-foreground',
              disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
            )}
          >
            {label}
          </label>
          {description && (
            <span className="text-xs leading-snug text-muted-foreground">{description}</span>
          )}
        </span>
      </div>
    );
  },
);
Switch.displayName = 'Switch';
